import { useEffect, useMemo, useState } from "react";
import { useMap } from "react-leaflet";
import { convertBitsToBytes } from "../../../../../utils/easyMaps/convertToBytes";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import L from "leaflet";
import * as ReactDOMServer from "react-dom/server";
import { useOutletContext } from "react-router-dom";
import { useMapa } from "../../../../../contexts/mapaContext";
import { SEM_DADOS, semLeitura } from "../../../../../utils/easyMaps/potenciaFtth";

interface OutletContextType {
  config: any;
}

type AnimationType = "pontilhada" | "tracejada" | "desativada";
type VariantType = "traffic" | "ftth" | "power";

interface PolylineData {
  outputTraffic?: number;
  inputTraffic?: number;
  powerTx?: number | string;
  powerRx?: number | string;
  color?: string;
}

interface PolylineCustomProps {
  host?: string;
  data?: PolylineData | null;
  positions: [number, number][];
  animation?: AnimationType;
  onClick?: (e: any) => void;
  dbClick?: (e: any) => void;
  percent?: number | null;
  isDown?: boolean;
  variant?: VariantType;
  darkMode?: boolean;
}

export function PolylineCustom(
  {
    host = "",
    data = null,
    positions,
    animation = "desativada",
    onClick = () => {},
    dbClick = () => {},
    percent = null,
    isDown = false,
    variant = "traffic",
  }: PolylineCustomProps,
  key?: string,
) {
  const { config } = useOutletContext<OutletContextType>();
  const theme = useTheme();
  const { configurationMap } = useMapa() as any;

  const [cor, setCor] = useState<string | null>(null);
  const styles = {
    fontPopup: {
      margin: "0px",
      fontSize: "10px",
      fontWeight: 500,
      color: theme.palette.color.textEasyMaps,
    },
    titlePopup: {
      margin: "0px",
      fontSize: "12px",
      fontWeight: 500,
      color: theme.palette.color.textEasyMaps,
      width: "100%",
      textAlign: "center" as const,
      marginBottom: "10px",
      whiteSpace: "normal" as const,
      overflow: "hidden",
      textOverflow: "ellipsis",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical" as const,
    },
  };

  const animationOp: Record<AnimationType, string | null> = {
    pontilhada: "1, 10",
    tracejada: "8, 13",
    desativada: null,
  };

  const map = useMap();

  const Content = () => {
    if (variant == "traffic") {
      const outPutTrafficData = convertBitsToBytes(data?.outputTraffic);
      const inPutTrafficData = convertBitsToBytes(data?.inputTraffic);
      return (
        <Stack
          key={key}
          style={{
            width: "150px",
          }}
        >
          <Typography style={styles.titlePopup}>{host}</Typography>
          <Box
            style={{
              display: "flex",
              flexDiretion: "row",
              justifyContent: "space-between",
            } as React.CSSProperties}
          >
            <Typography style={styles.fontPopup}>TX:</Typography>
            <Typography style={styles.fontPopup}>
              {outPutTrafficData.valor + " " + outPutTrafficData.unidade}
            </Typography>
          </Box>
          <Box
            style={{
              display: "flex",
              flexDiretion: "row",
              justifyContent: "space-between",
            } as React.CSSProperties}
          >
            <Typography style={styles.fontPopup}>RX:</Typography>
            <Typography style={styles.fontPopup}>
              {inPutTrafficData.valor + " " + inPutTrafficData.unidade}
            </Typography>
          </Box>
        </Stack>
      );
    } else if (variant == "ftth" || variant == "power") {
      return (
        <Stack
          key={key}
          style={{
            width: "150px",
          }}
        >
          <Typography style={styles.titlePopup}>{host}</Typography>
          <Box
            style={{
              display: "flex",
              flexDiretion: "row",
              justifyContent: "space-between",
            } as React.CSSProperties}
          >
            <Typography style={styles.fontPopup}>TX:</Typography>
            <Typography style={styles.fontPopup}>
              {semLeitura(data?.powerTx) ? SEM_DADOS : data?.powerTx + " dBm"}
            </Typography>
          </Box>
          <Box
            style={{
              display: "flex",
              flexDiretion: "row",
              justifyContent: "space-between",
            } as React.CSSProperties}
          >
            <Typography style={styles.fontPopup}>RX:</Typography>
            <Typography style={styles.fontPopup}>
              {semLeitura(data?.powerRx) ? SEM_DADOS : data?.powerRx + " dBm"}
            </Typography>
          </Box>
        </Stack>
      );
    } else return null;
  };

  const popup = L.tooltip([0, 0] as L.LatLngTuple, {
    permanent: false,
    direction: "top",
    offset: [0, -5],
    opacity: 0.9,
  });

  useEffect(() => {
    const isInvalidInput =
      percent == null ||
      percent === 0 ||
      percent === Infinity ||
      !config?.configuracoes?.ftth?.camadaFTTH?.escala;
    if (isInvalidInput) {
      setCor(theme.palette.enlaces.offline.main);
      return;
    }

    const escalasMap: Record<VariantType, any[]> = {
      traffic: config?.configuracoes?.ftth?.camadaFTTH?.escala,
      power: config?.configuracoes?.ftth?.camadaFTTH?.escala,
      ftth: config?.configuracoes?.ftth?.camadaFTTH?.escala,
    };

    const escalaCor = escalasMap[variant];
    if (escalaCor) {
      const corCorrespondente = escalaCor
        .filter((escala: any) =>
          variant === "traffic"
            ? parseFloat(escala?.label) <= (percent as number)
            : parseFloat(escala?.label) >= (percent as number),
        )
        .pop();

      setCor(corCorrespondente?.cor || theme.palette.enlaces.offline.main);
    } else {
      setCor(theme.palette.enlaces.offline.main);
    }
  }, [percent, config, variant, theme]);

  useEffect(() => {
    const line = L.polyline(positions, {
      color: data?.color || cor || undefined,
      weight: 3,
      dashArray: animationOp[animation] ?? undefined,
      renderer: L.svg(),
    }).addTo(map);

    const lineBase = L.polyline(positions, {
      color: "transparent",
      weight: 5,
      renderer: L.svg(),
    }).addTo(map);

    popup.setContent(ReactDOMServer.renderToString(<Content />));

    lineBase.on("click", onClick);
    lineBase.on("dblclick", dbClick);
    lineBase.on("mouseover mousemove", (e: any) => {
      popup.setLatLng(e.latlng).openOn(map);
    });
    lineBase.on("mouseout mouseleave", () => {
      map.removeLayer(popup);
    });

    let offset = 0;
    let currentColors = cor;
    let lastChangeTime = 0;
    let animationFrameId: number;

    const animateDown = (timestamp: number) => {
      if (timestamp - lastChangeTime > 400) {
        currentColors =
          currentColors !== theme.palette.enlaces.p90.main
            ? theme.palette.enlaces.p90.main
            : theme.palette.enlaces.offline.main;
        lastChangeTime = timestamp;
      }
      line.setStyle({ color: currentColors ?? undefined });
      animationFrameId = requestAnimationFrame(animateDown);
    };

    const animate = () => {
      offset -= 0.5;
      line.setStyle({ dashOffset: String(offset) });
      animationFrameId = requestAnimationFrame(animate);
    };

    // isDown entra por conta própria: ONU caída sem leitura (NaN) chegava como
    // -40 e piscava; tem de continuar piscando
    if (data?.powerRx || data?.powerTx || data?.inputTraffic || isDown) {
      if (isDown) {
        animationFrameId = requestAnimationFrame(animateDown);
      } else if (
        animationOp[animation] !== null &&
        configurationMap?.configuracoes?.animacao?.linha !== false
      ) {
        animationFrameId = requestAnimationFrame(animate);
      }
    }

    return () => {
      map.removeLayer(lineBase);
      map.removeLayer(line);
      map.removeLayer(popup);

      lineBase.off("click", onClick);
      lineBase.off("dblclick", dbClick);
      lineBase.off("mouseover mousemove");
      lineBase.off("mouseout mouseleave");

      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [key, map, positions, animation, configurationMap, cor]);

  return null;
}
